import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { updateProfile } from '../auth/authSlice';

/**
 * Hook condiviso per leggere/cambiare la lingua dell'app — single source of
 * truth (`feedback_one_way_only`) per `TopBar` (toggle text post-login) e
 * `LanguageSelector` (bandierine sulla LoginPage).
 *
 * Persiste la scelta in `localStorage` con la chiave `crm_language` (vedi
 * `src/i18n.ts` che inizializza i18next da quella chiave con default `'it'`).
 *
 * Lingue supportate oggi: `'it'`, `'en'`. Estendere qui se nuove lingue.
 */
export function useLanguage() {
  const { i18n } = useTranslation();

  const language = i18n.language;

  function changeLanguage(next: string) {
    if (next === language) return;
    i18n.changeLanguage(next);
    localStorage.setItem('crm_language', next);
  }

  return { language, changeLanguage };
}

// Lingua app (i18next) -> User.languageId (formato Liferay, locali del CRM).
const LANGUAGE_IDS: Record<string, string> = { it: 'it_IT', en: 'en_US' };

/**
 * Tiene il `languageId` del profilo allineato alla lingua dell'app: il backend
 * traduce gli errori nella lingua del PROFILO, e senza questo allineamento chi
 * sceglie IT (bandierine del login o toggle della TopBar) li riceverebbe nella
 * lingua del profilo. Vince la lingua dell'app, per scelta (TS-103).
 *
 * Da montare UNA volta, dentro il layout autenticato (TopBar): ogni consumer in
 * più manderebbe la sua PATCH. Dopo il salvataggio il reducer di updateProfile
 * aggiorna `user.languageId`, e l'effetto si ferma da solo.
 */
export function useProfileLanguageSync() {
  const { language } = useLanguage();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);

  useEffect(() => {
    const languageId = LANGUAGE_IDS[language];
    if (!user || !languageId || user.languageId === languageId) return;

    // L'endpoint vuole il profilo intero: nome, cognome ed email restano quelli attuali.
    void dispatch(updateProfile({
      firstName: user.firstName,
      lastName: user.lastName,
      emailAddress: user.emailAddress,
      languageId,
    }))
      .unwrap()
      .catch(() => {
        // Best-effort: se fallisce resta la lingua del profilo per i soli errori del server.
      });
  }, [language, user, dispatch]);
}
